import { useState } from "react";
import { Send, CheckCircle2, Star } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { AutoTextarea, Pills, TextField } from "@/components/tools/fields";

const FOUNDING = ["Yes", "No", "Tell me more"].map((v) => ({ v, label: v }));
const INIT = { rating: 0, ideas: "", email: "", founding: "", website: "" };

const Stars = ({ value, onChange }) => (
  <fieldset>
    <legend className="field-label mb-2">Was this pack useful?</legend>
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <label key={n} className="cursor-pointer rounded-lg p-1 focus-within:outline focus-within:outline-[3px] focus-within:outline-[var(--focus)]" data-testid={`qs-feedback-star-${n}`}>
          <input type="radio" name="qs-stars" value={n} checked={value === n} onChange={() => onChange(n)} className="sr-only" />
          <span className="sr-only">{n} out of 5 stars</span>
          <Star size={34} aria-hidden className={n <= value ? "fill-sun text-sun" : "text-ink-soft"} />
        </label>
      ))}
    </div>
  </fieldset>
);

export const QuickFeedbackForm = () => {
  const [f, setF] = useState(INIT);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.rating && !f.ideas.trim()) {
      setError("Please choose a star rating or tell us what would help.");
      return;
    }
    setStatus("sending");
    setError("");
    try {
      await api.post("/feedback", { pack: "quick-start", rating: f.rating || null, build_next: f.ideas, email: f.email || null, founding: f.founding, website: f.website });
      setStatus("sent");
      setF(INIT);
    } catch (err) {
      setError(errorText(err));
      setStatus("idle");
    }
  };
  if (status === "sent") {
    return (
      <div role="status" className="flex flex-col items-start gap-4 py-8" data-testid="qs-feedback-success">
        <CheckCircle2 size={44} className="text-brand" aria-hidden />
        <h3 className="font-display text-3xl font-medium">Thank you. Your feedback has been sent.</h3>
        <button type="button" className="btn btn-ghost" onClick={() => setStatus("idle")} data-testid="qs-feedback-again">Send more feedback</button>
      </div>
    );
  }
  return (
    <form onSubmit={submit} noValidate className="space-y-6" data-testid="qs-feedback-form">
      <Stars value={f.rating} onChange={set("rating")} />
      <AutoTextarea id="qf-ideas" label="What additional tools would help your setting?" rows={4} value={f.ideas} onChange={set("ideas")} testid="qs-feedback-ideas" />
      <TextField id="qf-email" label="Email (optional)" type="email" autoComplete="email" value={f.email} onChange={set("email")} testid="qs-feedback-email" />
      <Pills legend="Would you like to hear about the Founding Schools Programme? (optional)" hideLegend={false} name="qf-founding" options={FOUNDING} value={f.founding} onChange={set("founding")} testid="qs-feedback-founding" />
      <div className="hidden" aria-hidden="true">
        <label htmlFor="qf-website">Website</label>
        <input id="qf-website" tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => set("website")(e.target.value)} />
      </div>
      <p className="text-sm text-ink-soft">Please do not submit pupil-identifiable information, special category data or confidential case information.</p>
      {error && <p role="alert" className="rounded-xl border-2 p-3 font-bold" style={{ borderColor: "var(--rag-red-fg)", color: "var(--rag-red-fg)", background: "var(--rag-red)" }} data-testid="qs-feedback-error">{error}</p>}
      <button type="submit" disabled={status === "sending"} className="btn btn-brand text-base disabled:opacity-70" data-testid="qs-feedback-submit">
        <Send size={16} aria-hidden /> {status === "sending" ? "Sending…" : "Submit feedback"}
      </button>
    </form>
  );
};
